import cx from "classnames";

import Icon from "common/components/Icon";
import LinkButton from "common/components/LinkButton";
import { iconForCategorySlug } from "common/constants/categoryIcons";
import { schemeForCategoryIndex } from "common/constants/carousels";

import { useSendPostHogProductListFilterChange } from "client/analytics/utils";
import { ProductListFilterSurface, ProductListSource } from "client/analytics/types";

import { ClientSubcategory, SearchFilterOption } from "../types";
import CategoryBrowseRow from "./CategoryBrowseRow";

import styles from "../styles/CategorySubcategoryFilter.module.less";

type CategorySubcategoryFilterProps = {
  categoryId: number;
  subcategories: ClientSubcategory[];
  /** The page's `categories` options, in result order. */
  categoryOptions: SearchFilterOption<number>[];
  selectedIds: number[];
  onChange: (categoryIds: number[]) => void;
};

/**
 * A tier-1 page's tier-2 categories as filter pills, in result order. A pill with nothing left
 * under the other filters drops out unless it's selected.
 */
const CategorySubcategoryFilter = ({
  categoryId,
  subcategories,
  categoryOptions,
  selectedIds,
  onChange,
}: CategorySubcategoryFilterProps) => {
  const sendFilterChange = useSendPostHogProductListFilterChange();
  const optionById = new Map(categoryOptions.map((option) => [option.value, option]));
  const positionById = new Map(categoryOptions.map((option, index) => [option.value, index]));
  const selected = new Set(selectedIds);
  const positionOf = (id: number) => positionById.get(id) ?? Number.MAX_SAFE_INTEGER;
  const shopable = subcategories
    .filter((subcategory) => selected.has(subcategory.id) || optionById.get(subcategory.id)?.count)
    .sort((a, b) => positionOf(a.id) - positionOf(b.id));

  if (!shopable.length) {
    return null;
  }

  const toggle = ({ id, position }: { id: number; position: number }) => {
    const isSelecting = !selected.has(id);
    const nextIds = isSelecting ? [...selectedIds, id] : selectedIds.filter((s) => s !== id);
    sendFilterChange({
      source: ProductListSource.CategoryPage,
      filter: "category",
      value: id,
      selected: isSelecting,
      selected_values: nextIds.filter((nextId) =>
        subcategories.some((subcategory) => subcategory.id === nextId),
      ),
      surface: ProductListFilterSurface.Subcategories,
      position,
      category_id: categoryId,
    });
    onChange(nextIds);
  };

  const renderPill = (subcategory: ClientSubcategory, index: number) => {
    // By rank among all the options, so a pill keeps its color when others drop out.
    const scheme = schemeForCategoryIndex(positionById.get(subcategory.id) ?? index);
    const isSelected = selected.has(subcategory.id);
    return (
      <LinkButton
        className={cx(styles.pill, isSelected && styles.selected)}
        onClick={() => toggle({ id: subcategory.id, position: index })}
        aria-pressed={isSelected}
      >
        <div className={styles.icon}>
          <svg className={styles.iconBackdrop} viewBox="0 0 2 2" aria-hidden="true">
            <circle cx="1" cy="1" r="1" fill={scheme.blob} />
          </svg>
          <Icon
            className={styles.iconGlyph}
            icon={iconForCategorySlug(subcategory.slug)}
            color={scheme.icon}
          />
        </div>
        <span className={styles.label}>{subcategory.name}</span>
      </LinkButton>
    );
  };

  return (
    <CategoryBrowseRow
      heading="Filter by category"
      isContentWidth={true}
      revealKey={selectedIds[0] ?? null}
      items={shopable.map((subcategory, index) => ({
        key: subcategory.id,
        node: renderPill(subcategory, index),
      }))}
    />
  );
};

export default CategorySubcategoryFilter;
